import React, { FC } from 'react';

import { styled, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';

import ExclamationMarkIcon from '~/assets/icons/ExclamationMarkIcon';
import Flag from '~/assets/icons/Flag';
import useTenantTranslation from '~/hooks/useTenantTranslation';
import { SMALL_MARGIN_PX, TINY_MARGIN_PX } from '~/theme';
import { Maybe } from '~/types';

interface IQuestionSlideHeader {
  caption: string;
  handleReportClick: () => void;
  info?: Maybe<string>;
}

const QuestionSlideHeader: FC<IQuestionSlideHeader> = ({
  caption,
  handleReportClick,
  info,
  ...rest
}) => {
  const { t } = useTenantTranslation();

  return (
    <CaptionRow {...rest}>
      <Caption variant="h4">{caption}</Caption>
      {info && (
        <Info data-testid="quizType">
          <InfoIcon />
          {info}
        </Info>
      )}
      {handleReportClick && (
        <ReportButton data-testid="reportButton" ml={!!info} onClick={handleReportClick}>
          <StyledFlag />
          &nbsp;{t('button.report')}
        </ReportButton>
      )}
    </CaptionRow>
  );
};

export default QuestionSlideHeader;

const CaptionRow = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-bottom: ${SMALL_MARGIN_PX};
  padding-bottom: ${TINY_MARGIN_PX};

  ${({ theme }) => theme.breakpoints.up('md')} {
    flex-direction: row;
    justify-content: space-between;
    border-bottom: 1px solid ${({ theme }) => alpha(theme.palette.common.text.white, 0.33)};
  }
`;
const Caption = styled(Typography)`
  display: flex;
  justify-content: space-between;
  padding-bottom: ${TINY_MARGIN_PX};
  margin-bottom: ${TINY_MARGIN_PX};
  font-size: 18px;
  color: inherit;
  border-bottom: 1px solid ${({ theme }) => alpha(theme.palette.common.white, 0.33)};

  ${({ theme }) => theme.breakpoints.up('sm')} {
    margin-bottom: ${TINY_MARGIN_PX};
    font-size: 18px;
  }

  ${({ theme }) => theme.breakpoints.up('md')} {
    padding-bottom: 0;
    margin-bottom: 0;
    border-bottom: none;
  }
`;
const Info = styled('span')`
  display: flex;
  align-items: center;
  font-size: 14px;
  font-weight: 400;
  color: inherit;
  line-height: 1;

  ${({ theme }) => theme.breakpoints.up('md')} {
    margin-left: auto;
  }
`;
const InfoIcon = styled(ExclamationMarkIcon)`
  width: 16px;
  height: 16px;
  min-width: 16px;
  margin-right: ${TINY_MARGIN_PX};
  color: inherit;
`;
const ReportButton = styled('button', {
  shouldForwardProp: (prop) => prop !== 'ml',
})<{ ml: boolean }>`
  display: flex;
  align-items: center;
  margin-left: ${({ ml }) => ml && SMALL_MARGIN_PX};
  min-height: 24px;
  background: none;
  border: none;
  padding: 0;
  color: inherit;
  font-size: 14px;
  cursor: pointer;
`;
const StyledFlag = styled(Flag)`
  color: inherit;
`;
